/**
 * ReverseGeocoder — calls OpenStreetMap Nominatim to convert lat/lon
 * to a human-readable street address. Stateless; may be called as
 * often as needed though Nominatim has rate limits (1 req/sec).
 *
 * CORS-enabled from the browser, no API key required.
 */
export class ReverseGeocoder {
    async lookup(lat, lon, lang = 'de') {
        const url = `${ReverseGeocoder.BASE}?format=json&lat=${lat}&lon=${lon}&accept-language=${lang}&zoom=18`;
        try {
            const r = await fetch(url);
            if (!r.ok)
                return null;
            const d = await r.json();
            const addr = d?.address ?? {};
            const street = addr.road ||
                addr.pedestrian ||
                addr.footway ||
                addr.cycleway ||
                addr.path ||
                d?.display_name?.split(',')?.[0] ||
                '?';
            const city = addr.city || addr.town || addr.village || addr.hamlet || null;
            // suburb / district = stadtteil-level anchor (e.g. "Waidmannsdorf"
            // when city="Klagenfurt am Wörthersee"). Critical for accurate
            // place-of-residence framing — without it the bot falls back to
            // the nearest neighborhood label in <places>, which can be
            // hundreds of meters off (St. Martin at 493m instead of
            // Waidmannsdorf at 0m).
            const suburb = addr.suburb || addr.city_district || addr.neighbourhood || null;
            // region = administrative state/province (e.g. "Kärnten", "Bavaria")
            // — major Geo-Anker for the bot, saves ~30-50s of geo-inference
            // when present alongside city. Nominatim returns this as
            // `state` (top level) or sometimes `county` for lower-tier admin.
            const region = addr.state || addr.county || null;
            const country = addr.country || null;
            // ISO-3166 alpha-2 (lowercase, e.g. 'at', 'hr') — drives the local-language
            // Wikipedia pick abroad: a German-guessed title 404s on the local wiki's
            // monuments, but the local wiki's GEOSEARCH finds them (photo incl.).
            const countryCode = (typeof addr.country_code === 'string' && addr.country_code.toLowerCase()) || null;
            return { street, city, suburb, region, country, countryCode, raw: d };
        }
        catch {
            return null;
        }
    }
    /**
     * Forward geocode: resolve a place name to coordinates.
     * Uses Nominatim search with optional proximity bias.
     */
    async geocode(name, nearLat, nearLon) {
        const params = new URLSearchParams({
            format: 'json',
            q: name,
            limit: '1',
        });
        if (nearLat !== undefined && nearLon !== undefined) {
            params.set('viewbox', `${nearLon - 0.1},${nearLat + 0.1},${nearLon + 0.1},${nearLat - 0.1}`);
            params.set('bounded', '0'); // prefer but don't restrict to viewbox
        }
        try {
            const r = await fetch(`https://nominatim.openstreetmap.org/search?${params}`);
            if (!r.ok)
                return null;
            const d = await r.json();
            if (d.length > 0 && d[0].lat && d[0].lon) {
                return { lat: parseFloat(d[0].lat), lon: parseFloat(d[0].lon) };
            }
            return null;
        }
        catch {
            return null;
        }
    }
}
ReverseGeocoder.BASE = 'https://nominatim.openstreetmap.org/reverse';
//# sourceMappingURL=ReverseGeocoder.js.map